import React, { createContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { IntlProvider } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { auth, fcmToken } from '../firebase';
import {
  getLoggedUserProps,
  fetchFriends,
  fetchRequests,
  fetchAllRequests,
  fetchStats,
  fetchFields,
} from './api';
import { addMessagingTokenToUser } from '../login/utils';
import { onCollectionChange } from '../utils';
import { SPORTS, ROLES } from '../constants';

import esJSON from '../locale/es.json';
import enJSON from '../locale/en.json';

export const StoreContext = createContext(null);

const Store = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [language, setLanguage] = useState(navigator.language);
  const [messages, setMessages] = useState(esJSON);

  const [allRequests, setAllRequests] = useState(null);
  const [requestsChanged, setRequestsChanged] = useState(null);
  const [updateUser, setUpdateUser] = useState(false);
  const [isStoreLoading, setIsStoreLoading] = useState(true);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [sports, setSports] = useState([SPORTS.FUTBOL, SPORTS.PADEL]);
  const [placesChanged, setPlacesChanged] = useState(null);
  const [fields, setFields] = useState([]);

  useEffect(() => {
    if (language.includes('es')) {
      setMessages(esJSON);
    } else {
      setMessages(enJSON);
    }
  }, []);

  useEffect(() => {
    onCollectionChange('requests', setRequestsChanged);
  }, []);

  useEffect(() => {
    fetchAllRequests().then(p => setAllRequests(p));
  }, [requestsChanged]);

  useEffect(() => {
    onCollectionChange('places', setPlacesChanged);
  }, []);

  useEffect(() => {
    fetchFields().then(p => setFields(p));
  }, [placesChanged]);

  const setUpUser = async loggedUser => {
    setIsUserLoading(true);
    const userProps = await getLoggedUserProps();
    if (!userProps) {
      navigate('/login');
    }
    const requests = await fetchRequests();
    const {
      role,
      nickname,
      age,
      position,
      languageSelected,
      level,
      messageToken,
      friendsIds,
      games,
      createdGames,
      tokens,
      photoURL,
      friendsList = [],
    } = userProps || {};

    if (friendsIds) {
      await fetchFriends(friendsIds).then(data => {
        userProps.friends = data;
      });
    }

    await fetchStats(loggedUser.uid).then(data => {
      let goals = 0;
      let absent = 0;
      data.forEach(d => {
        goals += d.goals;
        if (d.absent) {
          absent++;
        }
      });
      userProps.goals = goals;
      userProps.absent = absent;
    });
    const isOwner = role === ROLES.OWNER;
    const isAdmin = role === ROLES.ADMIN;
    await addMessagingTokenToUser(fcmToken, loggedUser.uid);
    await 2000;
    setIsUserLoading(false);
    if (languageSelected) {
      if (languageSelected.includes('es')) {
        setMessages(esJSON);
      } else {
        setMessages(enJSON);
      }
    }
    setUser({
      ...loggedUser,
      role,
      games,
      createdGames,
      nickname,
      age,
      position,
      languageSelected,
      level,
      friends: userProps.friends,
      goals: userProps.goals,
      absent: userProps.absent,
      friendsIds,
      requests,
      isOwner,
      isAdmin,
      messageToken,
      tokens,
      photoURL,
      friendsList,
    });
  };

  useEffect(() => {
    onAuthStateChanged(auth, async loggedUser => {
      setIsStoreLoading(true);
      if (loggedUser) {
        await setUpUser(loggedUser);
      } else setUser(null);
      setIsStoreLoading(false);
    });
  }, []);

  useEffect(() => {
    if (user) {
      setUpUser(user);
    }
    setUpdateUser(false);
  }, [updateUser]);

  const store = {
    user: [user, setUser, setUpdateUser],
    sports: [sports, setSports],
    places: [fields, setFields],
    allRequests: [allRequests, setAllRequests],
    isStoreLoading: [isStoreLoading, setIsStoreLoading],
    isUserLoading: [isUserLoading, setIsUserLoading],
    language: [language, setLanguage],
  };

  const { Provider } = StoreContext;
  return (
    <Provider value={store}>
      <IntlProvider messages={messages} locale={language} defaultLocale="es">
        {children}
      </IntlProvider>
    </Provider>
  );
};

export default Store;
